import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Target, Eye, Sprout } from "lucide-react";
import "./About.css";

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="container about-hero-content">
          <h1 className="animate-on-scroll">Our Story</h1>
          <p className="animate-on-scroll">
            Rooted in care, responsibility, and a commitment to purity
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section story-section">
        <div className="container">
          <div className="story-content animate-on-scroll">
            <div className="story-text">
              <h2>Where It All Began</h2>
              <p>
                GAB Organics was born from a simple yet profound desire: to provide families with organic products they can truly trust. Founded in 2025 in Trivandrum, Kerala, our journey began with a personal commitment to clean, responsible food choices.
              </p>
              <p>
                We understand that every family deserves access to pure, wholesome products—free from compromise, full of integrity. That belief drives everything we do, from selecting our farmers to delivering products to your doorstep.
              </p>
              <p>
                Our story is one of care, responsibility, and conscious living. It's about building trust through transparency and delivering quality that speaks for itself.
              </p>
            </div>
            <div className="story-image">
              <img 
                src="https://images.pexels.com/photos/31737301/pexels-photo-31737301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Our journey" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section mission-vision-section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="mission-card animate-on-scroll">
              <div className="mv-icon">
                <Target size={50} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To provide thoughtfully sourced organic products that families can trust. We believe in making conscious living accessible, authentic, and rooted in integrity.
              </p>
            </div>
            <div className="vision-card animate-on-scroll">
              <div className="mv-icon">
                <Eye size={50} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To grow into a globally trusted organic brand while staying true to our roots. We envision a world where purity, responsibility, and quality are never compromised.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sourcing Philosophy */}
      <section className="section sourcing-section">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our Sourcing Philosophy</h2>
            <p>How we ensure quality and purity in every product</p>
          </div>
          <div className="sourcing-content">
            <div className="sourcing-image animate-on-scroll">
              <img 
                src="https://images.pexels.com/photos/28800915/pexels-photo-28800915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Organic farming" 
              />
            </div>
            <div className="sourcing-text animate-on-scroll">
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Carefully Selected Farmers</h4>
                  <p>We partner with trusted farmers who share our dedication to organic practices and sustainable agriculture.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>EU-Certified Suppliers</h4>
                  <p>Our supply sources meet stringent EU organic certification standards, ensuring uncompromising quality.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Reputed Vendors</h4>
                  <p>Every vendor we work with is thoroughly vetted to guarantee authenticity and reliability.</p>
                </div>
              </div>
              <div className="sourcing-point">
                <CheckCircle size={28} />
                <div>
                  <h4>Quality-First Approach</h4>
                  <p>From farm to family, we never compromise on the purity and integrity of our products.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profile */}
      <section className="section founder-profile-section">
        <div className="container">
          <div className="founder-profile animate-on-scroll">
            <div className="founder-profile-image">
              <div className="founder-placeholder">
                <Sprout size={80} />
              </div>
            </div>
            <div className="founder-profile-text">
              <h2>Meet the Founder</h2>
              <h3>Vineeth Abraham Koodathumannil</h3>
              <p className="founder-title">Founder, GAB Organics</p>
              
              <div className="founder-bio">
                <p>
                  With a solid background in Microbiology and international professional experience in structured food environments, Vineeth brings both scientific rigor and genuine care to GAB Organics.
                </p>
                <p>
                  Having worked in diverse food industry settings across the globe, Vineeth understands the importance of quality, safety, and integrity in every aspect of food production and sourcing.
                </p>
                <p>
                  Founded in 2025 in Trivandrum, Kerala, GAB Organics is Vineeth's personal mission to provide his family—and yours—with organic products that embody the highest standards of purity and trust.
                </p>
                <p>
                  Driven by a deep commitment to clean, responsible food choices, Vineeth personally oversees every aspect of GAB Organics, ensuring that each product meets his exacting standards before it reaches your home.
                </p>
              </div>
              
              <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
